import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Icon } from 'lego-on-react';
import { memoize } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import {
    RateCorrectionConditionChooserItem,
    RateCorrectionConditionChooserItemsGroup
} from '../../typings/ratesCorrections';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import {
    RateCorrectionConditionChooser,
    RateCorrectionConditionChooserView
} from '../RateCorrectionConditionChooser/RateCorrectionConditionChooser';
import { ScopedPopup } from '../ScopedPopup/ScopedPopup';

import './RateCorrectionConditionSelector.css';
import * as keyset from './RateCorrectionConditionSelector.i18n';

const i18n = i18nFactory(keyset);

const titleSelectButton = i18n('choose');

export interface RateCorrectionConditionChooserState {
    isOpen: boolean;
}

export interface RateCorrectionConditionChooserProps {
    cls?: string;
    items: Array<RateCorrectionConditionChooserItemsGroup>;
    value: Array<string>;
    disabledItems?: Array<string>;
    hideDisabledItems?: boolean;
    isDisabled?: boolean;
    multiSelect?: boolean;
    onChange: (values: Array<string>) => void;
    view: RateCorrectionConditionChooserView;
    valid?: boolean;
}

const blockClass = 'rate-correction-condition-selector';

export class RateCorrectionConditionSelector extends React.PureComponent<
    RateCorrectionConditionChooserProps,
    RateCorrectionConditionChooserState
> {
    private switcher = React.createRef<HTMLDivElement>();

    constructor(props: RateCorrectionConditionChooserProps) {
        super(props);

        this.state = {
            isOpen: false
        };
    }

    render() {
        const {
            cls,
            items,
            disabledItems,
            value,
            hideDisabledItems,
            view,
            multiSelect = false
        } = this.props;
        const { isOpen } = this.state;

        return (
            <div
                className={classNames(
                    Bem.cls(blockClass),
                    cls,
                    TestBem.cls(blockClass)
                )}
                ref={this.switcher}
            >
                {this.getOpener()}

                <ScopedPopup
                    theme="normal"
                    hiding={true}
                    target={'anchor'}
                    anchor={this.switcher.current}
                    visible={isOpen}
                    onOutsideClick={this.closeVisible}
                >
                    <RateCorrectionConditionChooser
                        items={items}
                        disabledItems={disabledItems}
                        hideDisabledItems={hideDisabledItems}
                        value={value}
                        view={view}
                        multiSelect={multiSelect}
                        onChange={this.onChange}
                    />
                </ScopedPopup>
            </div>
        );
    }

    private getOpener = () => {
        const {
            items: listGroups,
            isDisabled,
            value,
            multiSelect,
            valid
        } = this.props;
        const { isOpen } = this.state;
        let titleButton = '';

        if (multiSelect) {
            titleButton = getControlTitleForMultiSelectRateCorrectionConditions(
                listGroups,
                value
            );
        } else {
            titleButton = getControlTitleForSingleSelectRateCorrectionConditions(
                listGroups,
                value
            );
        }

        return (
            <ButtonDecorator
                cls={Bem.cls(blockClass, 'control-button')}
                theme="normal"
                view="classic"
                size="s"
                onClick={this.toggleVisible}
                disabled={isDisabled}
                width={'max'}
                iconRight={
                    <Icon
                        size={'n'}
                        type={'arrow'}
                        direction={isOpen ? 'top' : 'bottom'}
                    />
                }
                valid={valid}
            >
                {titleButton}
            </ButtonDecorator>
        );
    };

    private onChange = (values: Array<string>) => {
        const { onChange, view } = this.props;

        if (view === RateCorrectionConditionChooserView.default) {
            this.setState({
                isOpen: false
            });
        }

        onChange(values);
    };

    private toggleVisible = () => {
        this.setState({
            isOpen: !this.state.isOpen
        });
    };

    private closeVisible = () => {
        this.setState({
            isOpen: false
        });
    };
}

const getControlTitleForMultiSelectRateCorrectionConditions = memoize(
    (
        listGroups: Array<RateCorrectionConditionChooserItemsGroup>,
        value: Array<string>
    ): string => {
        return (
            listGroups
                .reduce((acc: string[], group) => {
                    acc = acc.concat(
                        group.items.reduce((accItem: string[], item) => {
                            if (value.includes(item.id)) {
                                accItem.push(item.name);
                            }

                            return accItem;
                        }, [])
                    );

                    return acc;
                }, [])
                .join(', ') || titleSelectButton
        );
    },
    (...args) => JSON.stringify(args)
);

const getControlTitleForSingleSelectRateCorrectionConditions = memoize(
    (
        listGroups: Array<RateCorrectionConditionChooserItemsGroup>,
        value: Array<string>
    ): string => {
        const currentItem = listGroups
            .reduce(
                (res: Array<RateCorrectionConditionChooserItem>, group) =>
                    res.concat(group.items),
                []
            )
            .find(item => item.id === value[0]);
        return currentItem ? currentItem.name : titleSelectButton;
    },
    (...args) => JSON.stringify(args)
);
